import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { DistrictMaster, AreaMaster, RegionMaster, CountryMaster } from '../../types/master';

interface Props {
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const DistrictMasterTab: React.FC<Props> = ({ onError, onSuccess }) => {
  const [districts, setDistricts] = useState<DistrictMaster[]>([]);
  const [areas, setAreas] = useState<AreaMaster[]>([]);
  const [regions, setRegions] = useState<RegionMaster[]>([]);
  const [countries, setCountries] = useState<CountryMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDistrict, setEditingDistrict] = useState<DistrictMaster | null>(null);
  const [filterCountry, setFilterCountry] = useState<string>('');
  const [filterRegion, setFilterRegion] = useState<string>('');
  const [filterArea, setFilterArea] = useState<string>('');
  const [formData, setFormData] = useState({
    countryCode: '',
    regionId: '',
    areaId: '',
    code: '',
    nameJa: '',
    nameTh: '',
    nameEn: '',
    sortOrder: 0,
    isActive: true,
  });

  const functions = getFunctions();

  useEffect(() => {
    fetchCountries();
    fetchDistricts();
  }, []);

  useEffect(() => {
    if (filterCountry) {
      fetchRegions(filterCountry);
      fetchDistricts();
    }
  }, [filterCountry]);

  useEffect(() => {
    if (filterRegion) {
      fetchAreas(filterRegion);
      fetchDistricts();
    }
  }, [filterRegion]);

  useEffect(() => {
    if (filterArea) {
      fetchDistricts();
    }
  }, [filterArea]);

  const fetchCountries = async () => {
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const result = await getMasterData({ type: 'countries' });
      const data = (result.data as any).data as CountryMaster[];
      setCountries(data);
    } catch (error: any) {
      onError(error.message || '国マスタの取得に失敗しました');
    }
  };

  const fetchRegions = async (countryCode?: string) => {
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const params: any = { type: 'regions' };
      if (countryCode) {
        params.countryCode = countryCode;
      }
      const result = await getMasterData(params);
      const data = (result.data as any).data as RegionMaster[];
      setRegions(data);
    } catch (error: any) {
      onError(error.message || '地方マスタの取得に失敗しました');
    }
  };

  const fetchAreas = async (regionId?: string) => {
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const params: any = { type: 'areas' };
      if (filterCountry) {
        params.countryCode = filterCountry;
      }
      if (regionId) {
        params.regionId = regionId;
      }
      const result = await getMasterData(params);
      const data = (result.data as any).data as AreaMaster[];
      setAreas(data);
    } catch (error: any) {
      onError(error.message || 'エリアマスタの取得に失敗しました');
    }
  };

  const fetchDistricts = async () => {
    setLoading(true);
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const params: any = { type: 'districts', includeInactive: true };
      if (filterCountry) {
        params.countryCode = filterCountry;
      }
      if (filterRegion) {
        params.regionId = filterRegion;
      }
      if (filterArea) {
        params.areaId = filterArea;
      }
      const result = await getMasterData(params);
      const data = (result.data as any).data as DistrictMaster[];
      setDistricts(data);
    } catch (error: any) {
      onError(error.message || '地区マスタの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (district?: DistrictMaster) => {
    if (district) {
      setEditingDistrict(district);
      setFormData({
        countryCode: district.countryCode,
        regionId: district.regionId,
        areaId: district.areaId,
        code: district.code,
        nameJa: district.name.ja,
        nameTh: district.name.th || '',
        nameEn: district.name.en || '',
        sortOrder: district.sortOrder,
        isActive: district.isActive,
      });
      // 地方・エリアリストを取得
      fetchRegions(district.countryCode);
      fetchAreas(district.regionId);
    } else {
      setEditingDistrict(null);
      const defaultCountry = filterCountry || countries[0]?.code || '';
      setFormData({
        countryCode: defaultCountry,
        regionId: filterRegion || '',
        areaId: filterArea || '',
        code: '',
        nameJa: '',
        nameTh: '',
        nameEn: '',
        sortOrder: districts.length + 1,
        isActive: true,
      });
      if (defaultCountry) {
        fetchRegions(defaultCountry);
      }
      if (filterRegion) {
        fetchAreas(filterRegion);
      }
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDistrict(null);
  };

  const handleCountryChange = (countryCode: string) => {
    setFormData({ ...formData, countryCode, regionId: '', areaId: '' });
    fetchRegions(countryCode);
  };

  const handleRegionChange = (regionId: string) => {
    setFormData({ ...formData, regionId, areaId: '' });
    fetchAreas(regionId);
  };

  const handleSave = async () => {
    try {
      if (!formData.countryCode || !formData.regionId || !formData.areaId || !formData.code || !formData.nameJa) {
        onError('必須項目を入力してください');
        return;
      }

      if (editingDistrict) {
        const updateMasterData = httpsCallable(functions, 'updateMasterData');
        await updateMasterData({
          id: editingDistrict.id,
          type: 'districts',
          data: {
            countryCode: formData.countryCode,
            regionId: formData.regionId,
            areaId: formData.areaId,
            code: formData.code,
            name: {
              ja: formData.nameJa,
              th: formData.nameTh || undefined,
              en: formData.nameEn || undefined,
            },
            sortOrder: formData.sortOrder,
            isActive: formData.isActive,
          },
        });
        onSuccess('地区マスタを更新しました');
      } else {
        const createDistrict = httpsCallable(functions, 'createDistrict');
        await createDistrict({
          countryCode: formData.countryCode,
          regionId: formData.regionId,
          areaId: formData.areaId,
          code: formData.code,
          name: {
            ja: formData.nameJa,
            th: formData.nameTh || undefined,
            en: formData.nameEn || undefined,
          },
          sortOrder: formData.sortOrder,
          isActive: formData.isActive,
        });
        onSuccess('地区マスタを作成しました');
      }

      handleCloseDialog();
      fetchDistricts();
    } catch (error: any) {
      onError(error.message || '保存に失敗しました');
    }
  };

  const handleDelete = async (district: DistrictMaster) => {
    if (!window.confirm(`${district.name.ja}を削除してもよろしいですか？`)) {
      return;
    }

    try {
      const deleteMasterData = httpsCallable(functions, 'deleteMasterData');
      const result = await deleteMasterData({
        id: district.id,
        type: 'districts',
      });
      onSuccess((result.data as any).message || '地区マスタを削除しました');
      fetchDistricts();
    } catch (error: any) {
      onError(error.message || '削除に失敗しました');
    }
  };

  const getCountryName = (countryCode: string) => {
    return countries.find(c => c.code === countryCode)?.name.ja || countryCode;
  };

  const getRegionName = (regionId: string) => {
    return regions.find(r => r.id === regionId)?.name.ja || regionId;
  };

  const getAreaName = (areaId: string) => {
    return areas.find(a => a.id === areaId)?.name.ja || areaId;
  };

  const filteredRegions = regions.filter(r =>
    !filterCountry || r.countryCode === filterCountry
  );

  const filteredAreas = areas.filter(a =>
    !filterRegion || a.regionId === filterRegion
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            地区を追加
          </Button>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>国</InputLabel>
            <Select
              value={filterCountry}
              label="国"
              onChange={(e) => {
                setFilterCountry(e.target.value);
                setFilterRegion('');
                setFilterArea('');
              }}
            >
              <MenuItem value="">全て</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>地方</InputLabel>
            <Select
              value={filterRegion}
              label="地方"
              onChange={(e) => {
                setFilterRegion(e.target.value);
                setFilterArea('');
                if (e.target.value) {
                  fetchAreas(e.target.value);
                }
              }}
              disabled={!filterCountry}
            >
              <MenuItem value="">全て</MenuItem>
              {filteredRegions.map((region) => (
                <MenuItem key={region.id} value={region.id}>
                  {region.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>エリア</InputLabel>
            <Select
              value={filterArea}
              label="エリア"
              onChange={(e) => setFilterArea(e.target.value)}
              disabled={!filterRegion}
            >
              <MenuItem value="">全て</MenuItem>
              {filteredAreas.map((area) => (
                <MenuItem key={area.id} value={area.id}>
                  {area.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>国</TableCell>
              <TableCell>地方</TableCell>
              <TableCell>エリア</TableCell>
              <TableCell>コード</TableCell>
              <TableCell>名称（日本語）</TableCell>
              <TableCell>名称（タイ語）</TableCell>
              <TableCell>名称（英語）</TableCell>
              <TableCell>表示順</TableCell>
              <TableCell>状態</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {districts.map((district) => (
              <TableRow key={district.id}>
                <TableCell>{getCountryName(district.countryCode)}</TableCell>
                <TableCell>{getRegionName(district.regionId)}</TableCell>
                <TableCell>{getAreaName(district.areaId)}</TableCell>
                <TableCell>{district.code}</TableCell>
                <TableCell>{district.name.ja}</TableCell>
                <TableCell>{district.name.th || '-'}</TableCell>
                <TableCell>{district.name.en || '-'}</TableCell>
                <TableCell>{district.sortOrder}</TableCell>
                <TableCell>
                  <Chip
                    label={district.isActive ? '有効' : '無効'}
                    color={district.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(district)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(district)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDistrict ? '地区マスタを編集' : '地区マスタを追加'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>国</InputLabel>
            <Select
              value={formData.countryCode}
              label="国"
              onChange={(e) => handleCountryChange(e.target.value)}
              disabled={!!editingDistrict}
            >
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" required>
            <InputLabel>地方</InputLabel>
            <Select
              value={formData.regionId}
              label="地方"
              onChange={(e) => handleRegionChange(e.target.value)}
              disabled={!formData.countryCode || !!editingDistrict}
            >
              {regions.map((region) => (
                <MenuItem key={region.id} value={region.id}>
                  {region.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" required>
            <InputLabel>エリア</InputLabel>
            <Select
              value={formData.areaId}
              label="エリア"
              onChange={(e) => setFormData({ ...formData, areaId: e.target.value })}
              disabled={!formData.regionId || !!editingDistrict}
            >
              {areas.map((area) => (
                <MenuItem key={area.id} value={area.id}>
                  {area.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="コード"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            margin="normal"
            required
            disabled={!!editingDistrict}
            placeholder="例: SHINJUKU_WEST"
          />
          <TextField
            fullWidth
            label="名称（日本語）"
            value={formData.nameJa}
            onChange={(e) => setFormData({ ...formData, nameJa: e.target.value })}
            margin="normal"
            required
            placeholder="例: 新宿西口"
          />
          <TextField
            fullWidth
            label="名称（タイ語）"
            value={formData.nameTh}
            onChange={(e) => setFormData({ ...formData, nameTh: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="名称（英語）"
            value={formData.nameEn}
            onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
            margin="normal"
            placeholder="例: Shinjuku West"
          />
          <TextField
            fullWidth
            label="表示順"
            type="number"
            value={formData.sortOrder}
            onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) })}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="有効"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DistrictMasterTab;
